// How money is written, everywhere in the UI.
//
// This existed as a copy-pasted one-liner in fourteen components and not at all in another
// fourteen, so the same figure appeared as "UGX 50,000.00", "UGX 50000", "50,000" or "50000"
// depending on which screen you were looking at.
//
// TWO THINGS ARE DELIBERATE HERE:
//
//   The locale is pinned to en-US, not left as `undefined`. `undefined` means "whatever the
//   viewer's browser is set to" — and on a machine set to German or French, toLocaleString
//   groups with dots or spaces and uses a comma for the DECIMAL point. 50,000 would render
//   as "50.000,00", which is not a formatting preference but a different number to anyone
//   reading it. The grouping separator is a property of the report, not of the reader.
//
//   The sign goes before the symbol. `(-50000).toLocaleString()` yields "-50,000", which
//   concatenated after a prefix reads "UGX -50,000" — the minus buried mid-string where it
//   is easy to miss on a credit line.

export const CURRENCY_SYMBOLS = Object.freeze({
  USD: '$',
  UGX: 'UGX ',
  KSH: 'Ksh ',
  TSH: 'Tsh '
});

export const DEFAULT_CURRENCY = 'UGX';

/** The prefix for a currency, falling back to the code itself for anything unrecognised. */
export function currencyPrefix(currency) {
  if (!currency) return CURRENCY_SYMBOLS[DEFAULT_CURRENCY];
  return CURRENCY_SYMBOLS[currency] || `${currency} `;
}

/**
 * Group a number for display: 50000 → "50,000.00", -50000 → "-50,000.00".
 *
 * THE SIGN IS KEPT. This used to apply Math.abs(), on the reasoning that formatMoney
 * re-adds the sign in front of the currency symbol — true, but this function is also
 * called directly for the balance column of the Balance Sheet, the Trial Balance and the
 * Chart of Accounts, and there the absolute value is not a formatting choice but a
 * different number.
 *
 * It mattered: total liabilities stood at -638,290 and the Balance Sheet printed
 * "638,290.00" beneath a green "Balanced" note, so assets appeared not to equal liabilities
 * plus equity and the figure that revealed the underlying fault was legible on exactly one
 * screen — the dashboard, which goes through formatMoney.
 *
 * formatMoney still places the sign before the symbol, and takes the absolute value itself
 * to do so.
 *
 * @param {*} amount            anything numeric; null, undefined and NaN read as 0
 * @param {number} opts.decimals  fixed number of decimal places (default 2)
 */
export function formatAmount(amount, { decimals = 2 } = {}) {
  const parsed = Number(amount);
  const safe = Number.isFinite(parsed) ? parsed : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * A money amount with its currency: 50000 → "UGX 50,000.00".
 *
 * @param {*} amount
 * @param {string} currency       ISO-ish code; unrecognised codes are used as the prefix
 * @param {number} opts.decimals  default 2 — pass 0 for headline figures where the cents
 *                                are noise rather than information
 * @param {boolean} opts.showSymbol  false for columns that carry the currency in the header
 */
export function formatMoney(amount, currency = DEFAULT_CURRENCY, { decimals = 2, showSymbol = true } = {}) {
  const parsed = Number(amount);
  const safe = Number.isFinite(parsed) ? parsed : 0;
  const sign = safe < 0 ? '-' : '';
  // Absolute value here, not in formatAmount — the sign is being placed by hand in front of
  // the symbol, so the body must not carry one too or a negative reads "-UGX -50,000.00".
  const body = formatAmount(Math.abs(safe), { decimals });
  return showSymbol ? `${sign}${currencyPrefix(currency)}${body}` : `${sign}${body}`;
}
